// Tipos de errores específicos para el manejo de errores
export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: unknown;
}

export interface ValidationError {
  message: string;
  field?: string;
  value?: unknown;
}

export interface AuthError extends ApiError {
  type: "auth";
  authCode?:
    | "UNAUTHORIZED"
    | "FORBIDDEN"
    | "TOKEN_EXPIRED"
    | "INVALID_CREDENTIALS";
}

export interface NetworkError {
  message: string;
  isNetworkError: true;
  statusCode?: number;
}

export interface BusinessError extends ApiError {
  type: "business";
  businessCode?: string;
}

export interface FirebaseError {
  code: string;
  message: string;
  details?: unknown;
}

export interface FileUploadError {
  message: string;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
}

// Union type para todos los errores conocidos
export type AppError =
  | ApiError
  | ValidationError
  | NetworkError
  | FirebaseError
  | FileUploadError
  | BusinessError
  | AuthError
  | Error;

// Helper para crear errores tipados
export const createApiError = (
  message: string,
  status?: number,
  code?: string
): ApiError => ({
  message,
  status,
  code,
});

export const createValidationError = (
  message: string,
  field?: string
): ValidationError => ({
  message,
  field,
});

export const createNetworkError = (
  message: string,
  statusCode?: number
): NetworkError => ({
  message,
  isNetworkError: true,
  statusCode,
});

export const createFileUploadError = (
  message: string,
  fileName?: string
): FileUploadError => ({
  message,
  fileName,
});

// Helper para determinar el tipo de error
export const isApiError = (error: unknown): error is ApiError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    "status" in error
  );
};

export const isValidationError = (error: unknown): error is ValidationError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    "field" in error
  );
};

export const isNetworkError = (error: unknown): error is NetworkError => {
  return (
    typeof error === "object" && error !== null && "isNetworkError" in error
  );
};

export const isFirebaseError = (error: unknown): error is FirebaseError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof (error as any).code === "string"
  );
};

export const isAuthError = (error: unknown): error is AuthError => {
  return isApiError(error) && "type" in error && error.type === "auth";
};

export const isBusinessError = (error: unknown): error is BusinessError => {
  return isApiError(error) && "type" in error && error.type === "business";
};

// Función para parsear errores de Axios/API
export const parseApiError = (error: unknown): AppError => {
  if (isApiError(error)) {
    return error as AppError;
  }

  // Error de Axios
  if (typeof error === "object" && error !== null && "response" in error) {
    const axiosError = error as any;
    const status = axiosError.response?.status || 500;
    const message =
      axiosError.response?.data?.message ||
      axiosError.message ||
      "Error desconocido";

    if (status === 401 || status === 403) {
      return {
        type: "auth",
        message,
        code: "AUTH_ERROR",
        status,
        authCode: status === 401 ? "UNAUTHORIZED" : "FORBIDDEN",
      } as AuthError;
    }

    if (status >= 500) {
      return {
        type: "network",
        message,
        code: "NETWORK_ERROR",
        status,
        isNetworkError: true,
      } as NetworkError;
    }

    return {
      type: "business",
      message,
      code: "BUSINESS_ERROR",
      status,
    } as BusinessError;
  }

  // Error genérico
  const message = error instanceof Error ? error.message : "Error desconocido";
  return {
    type: "business",
    message,
    code: "UNKNOWN_ERROR",
    status: 500,
  } as BusinessError;
};
